import { useEffect, useState } from 'react';
import { FormGroup, Input, Label, Spinner } from 'reactstrap';
import * as emrInjector from '../EMR_Integration/react_webInjector.ts';
import {
  ActionButton,
  bodyFontFamily,
  COLORS,
  Disclosure,
  JsonRow,
  JSONBlock,
  JSONValidityPill,
  monoFontFamily,
  StatChip,
  StatusBadge,
  styles,
} from '../components/Misc/EMRInjectorDebugUI';

const isPlainObject = (value) =>
  value != null && typeof value === 'object' && !Array.isArray(value);

const getQuestionCardBackground = (question) => {
  if (question._aiStatus === 'error' || question._insertStatus === 'error') {
    return COLORS.errorSoft;
  }
  if (question._aiStatus === 'pending' || question._insertStatus === 'pending') {
    return COLORS.pendingSoft;
  }
  return COLORS.surface;
};

const formatAnswerForEditor = (question, answer) => {
  if (answer == null) return '';
  if (question.Type === 'Flex' && typeof answer === 'object') {
    return JSON.stringify(answer, null, 2);
  }
  return String(answer);
};

const parseFlexAnswer = (question) => {
  const answerText = question._manualAnswer?.trim() ?? '';

  if (!answerText) {
    return {
      parsedAnswer: Array.isArray(question.Structure) ? [] : {},
      parseError: null,
    };
  }

  try {
    return {
      parsedAnswer: JSON.parse(answerText),
      parseError: null,
    };
  } catch {
    return {
      parsedAnswer: null,
      parseError: 'Flex answers must be valid JSON before inserting.',
    };
  }
};

const getAnswerForInsert = (question) => {
  if (question.Type !== 'Flex') {
    return question._manualAnswer ?? '';
  }

  const { parsedAnswer, parseError } = parseFlexAnswer(question);
  if (parseError) {
    throw new Error(parseError);
  }
  return parsedAnswer;
};

const buildInsertRequest = (question, answer, replyType) => ({
  type: 'PUSH_ANSWER',
  args: {
    Question: question.Question,
    Answer: answer,
    'Reply Type': replyType,
  },
});

const getInsertErrorFromResult = (result) => {
  if (result === true || result == null) return null;
  if (result === false) return 'Injector returned false.';
  if (typeof result === 'string') return result;
  if (Array.isArray(result)) {
    return result.length === 0 ? null : `Injector returned: ${JSON.stringify(result)}`;
  }
  if (isPlainObject(result)) {
    if (typeof result.error === 'string') return result.error;
    if (typeof result.Error === 'string') return result.Error;
    if (result.success === false) {
      return result.message || 'Injector did not confirm a successful insert.';
    }
  }
  return null;
};

const stripPrivateFields = (sourceObject) =>
  Object.fromEntries(
    Object.entries(sourceObject).filter(([fieldName]) => !fieldName.startsWith('_')),
  );

const buildQuestionJSONValue = (question) => stripPrivateFields(question);

const buildGetAnswersRequestValue = (questions) => ({
  answers: questions.map((question, questionIndex) => {
    const replyType = question['Reply Type'] ?? question.ReplyType ?? null;

    if (question.Type === 'Flex') {
      const { parsedAnswer, parseError } = parseFlexAnswer(question);
      return {
        index: questionIndex,
        Key: question.Key ?? null,
        Question: question.Question ?? null,
        Type: question.Type ?? null,
        'Reply Type': replyType,
        Answer: parsedAnswer,
        'Raw Editor Value': question._manualAnswer ?? '',
        parseError,
      };
    }

    return {
      index: questionIndex,
      Key: question.Key ?? null,
      Question: question.Question ?? null,
      Type: question.Type ?? null,
      'Reply Type': replyType,
      Answer: question._manualAnswer ?? '',
    };
  }),
});

const hasInsertReturnValue = (question) =>
  question._insertResult !== null && question._insertResult !== undefined;

const shouldShowInsertResult = (question) =>
  hasInsertReturnValue(question) || question._insertStatus === 'success';

const getInsertResultDisplayValue = (question) =>
  hasInsertReturnValue(question) ? question._insertResult : 'No return value';

const QuestionCard = ({
  question,
  questionIndex,
  canAnswer,
  isBulkAI,
  isBulkInsert,
  onAskAI,
  onInsert,
  onManualChange,
  onToggleCardOpen,
}) => {
  const replyType = question['Reply Type'] ?? question.ReplyType ?? null;
  const isCardOpen = question._cardOpen !== false;
  const questionKeyLabel = question.Key ?? `#${questionIndex + 1}`;
  const aiDisabled = question._aiStatus === 'pending' || isBulkAI || !canAnswer;
  const insertDisabled = question._insertStatus === 'pending' || isBulkInsert;
  const flexAnswerState =
    question.Type === 'Flex' ? parseFlexAnswer(question) : { parsedAnswer: null, parseError: null };

  return (
    <div
      style={{
        ...styles.card,
        backgroundColor: getQuestionCardBackground(question),
        borderColor:
          question._aiStatus === 'error' || question._insertStatus === 'error'
            ? COLORS.error
            : COLORS.border,
      }}
    >
      <div
        style={{
          ...styles.cardHeader,
          marginBottom: isCardOpen ? '0.7rem' : 0,
        }}
      >
        <div style={styles.cardHeaderLeft}>
          <button
            type="button"
            onClick={() => onToggleCardOpen(questionIndex)}
            aria-label={isCardOpen ? 'Collapse question' : 'Expand question'}
            style={styles.chevronButton}
          >
            {isCardOpen ? '▾' : '▸'}
          </button>

          <div style={{ minWidth: 0, flex: '1 1 auto' }}>
            <div style={styles.badgeRow}>
              <StatusBadge
                status={question._aiStatus}
                idleLabel="ai idle"
                successLabel="answered"
                pendingLabel="thinking"
                errorLabel="failed"
              />
              <StatusBadge
                status={question._insertStatus}
                idleLabel="insert idle"
                successLabel="returned"
                pendingLabel="sending"
                errorLabel="failed"
              />
              <span
                style={{
                  ...styles.badge,
                  backgroundColor: COLORS.surfaceAlt,
                  color: COLORS.subtle,
                }}
              >
                #{questionIndex + 1}
                {question.Type ? ` · ${question.Type}` : ''}
                {replyType ? ` · ${replyType}` : ''}
              </span>
            </div>

            <div
              style={{
                ...styles.questionKey,
                marginBottom: isCardOpen && question.Question ? '0.15rem' : 0,
              }}
            >
              {questionKeyLabel}
            </div>

            {isCardOpen && question.Question && (
              <div style={styles.questionText}>{question.Question}</div>
            )}
          </div>
        </div>

        {isCardOpen && (
          <div style={styles.cardHeaderRight}>
            <ActionButton
              variant="primaryOutline"
              disabled={aiDisabled}
              busy={question._aiStatus === 'pending'}
              onClick={() => onAskAI(questionIndex)}
              title={canAnswer ? undefined : 'Select a conversation above first'}
              style={{ minWidth: '82px' }}
            >
              Ask AI
            </ActionButton>
            <ActionButton
              variant="success"
              disabled={insertDisabled}
              busy={question._insertStatus === 'pending'}
              onClick={() => onInsert(questionIndex)}
              style={{ minWidth: '98px' }}
            >
              Insert into EMR
            </ActionButton>
          </div>
        )}
      </div>

      {isCardOpen && (
        <>
          <Disclosure label="Question JSON">
            <JSONBlock value={buildQuestionJSONValue(question)} />
          </Disclosure>

          <Disclosure
            label={
              <>
                <span>Answer workspace</span>
                {question.Type === 'Flex' && (
                  <JSONValidityPill isValid={!flexAnswerState.parseError} />
                )}
              </>
            }
            defaultOpen
          >
            <Input
              type="textarea"
              bsSize="sm"
              rows={question.Type === 'Flex' ? 7 : 3}
              value={question._manualAnswer ?? ''}
              onChange={(event) => onManualChange(questionIndex, event.target.value)}
              placeholder={
                question._aiStatus === 'pending'
                  ? 'Asking AI…'
                  : question.Type === 'Flex'
                    ? 'Flex answer JSON (Ask AI or edit manually)'
                    : 'Answer (Ask AI or type manually)'
              }
              disabled={question._aiStatus === 'pending'}
              style={{
                ...styles.textArea,
                opacity: question._aiStatus === 'pending' ? 0.6 : 1,
                fontFamily: question.Type === 'Flex' ? monoFontFamily : bodyFontFamily,
              }}
            />

            {question.Type === 'Flex' && flexAnswerState.parseError && (
              <div style={{ ...styles.message, color: COLORS.error }}>
                {flexAnswerState.parseError}
              </div>
            )}

            {question._aiStatus === 'error' && (
              <div style={{ ...styles.message, color: COLORS.error }}>
                AI: {question._aiError || 'Error'}
              </div>
            )}

            {question._insertStatus === 'error' && (
              <div style={{ ...styles.message, color: COLORS.error }}>
                Insert: {question._insertError || 'Failed'}
              </div>
            )}

            {question._insertStatus === 'success' && (
              <div style={{ ...styles.message, color: COLORS.subtle }}>Insert call returned.</div>
            )}
          </Disclosure>

          <Disclosure label="Requests & responses">
            <JsonRow
              label="Insert Request (PUSH_ANSWER)"
              value={question._insertRequest}
              boxed={false}
            />
            {shouldShowInsertResult(question) && (
              <JsonRow
                label="Insert Result"
                value={getInsertResultDisplayValue(question)}
                boxed={false}
              />
            )}
            <JsonRow label="AI Request" value={question._aiRequest} boxed={false} />
            <JsonRow label="AI Response" value={question._aiResponse} boxed={false} />
          </Disclosure>
        </>
      )}
    </div>
  );
};

const EMRInjectorDebug = ({ apiClient }) => {
  const [conversations, setConversations] = useState([]);
  const [patientMap, setPatientMap] = useState({});
  const [conversationId, setConversationId] = useState('');
  const [conversationsLoading, setConversationsLoading] = useState(false);

  const [pageInfo, setPageInfo] = useState(null);
  const [patientInfo, setPatientInfo] = useState(null);
  const [questionsResponse, setQuestionsResponse] = useState(null);
  const [questions, setQuestions] = useState([]);

  const [isLoadingPage, setIsLoadingPage] = useState(false);
  const [isLoadingPatient, setIsLoadingPatient] = useState(false);
  const [isLoadingQuestions, setIsLoadingQuestions] = useState(false);
  const [isBulkAI, setIsBulkAI] = useState(false);
  const [isBulkInsert, setIsBulkInsert] = useState(false);

  useEffect(() => {
    let cancelled = false;

    setConversationsLoading(true);
    Promise.allSettled([apiClient.updateConversations(), apiClient.updatePatients()]).then(() => {
      if (cancelled) return;

      setConversations([...(apiClient.conversations || [])]);

      const nextPatientMap = {};
      (apiClient.patients || []).forEach((patient) => {
        if (!patient.uid) return;
        const patientLabel = [patient.lastName, patient.firstName].filter(Boolean).join(', ');
        nextPatientMap[patient.uid] = patientLabel || patient.uid;
      });

      setPatientMap(nextPatientMap);
      setConversationsLoading(false);
    });

    return () => {
      cancelled = true;
    };
  }, [apiClient]);

  const updateQuestion = (index, patch) => {
    setQuestions((previousQuestions) =>
      previousQuestions.map((question, questionIndex) =>
        questionIndex === index ? { ...question, ...patch } : question,
      ),
    );
  };

  const fetchPageInfo = async () => {
    setIsLoadingPage(true);
    try {
      setPageInfo(await emrInjector.getPageInfo());
    } catch (error) {
      setPageInfo({ error: error?.message });
    } finally {
      setIsLoadingPage(false);
    }
  };

  const fetchPatientInfo = async () => {
    setIsLoadingPatient(true);
    try {
      setPatientInfo(await emrInjector.getPatientInfo());
    } catch (error) {
      setPatientInfo({ error: error?.message });
    } finally {
      setIsLoadingPatient(false);
    }
  };

  const fetchQuestions = async () => {
    setIsLoadingQuestions(true);
    try {
      const questionResponse = await emrInjector.getQuestionsFromEMR();
      const scrapedQuestions = Array.isArray(questionResponse)
        ? questionResponse
        : questionResponse?.questions ?? [];

      setQuestionsResponse(questionResponse);
      setQuestions(
        scrapedQuestions.map((question) => ({
          ...question,
          _cardOpen: true,
          _aiStatus: null,
          _aiAnswer: null,
          _aiError: null,
          _aiRequest: null,
          _aiResponse: null,
          _manualAnswer: '',
          _insertStatus: null,
          _insertError: null,
          _insertRequest: null,
          _insertResult: null,
        })),
      );
    } catch (error) {
      console.error('Failed to scrape questions:', error);
      setQuestionsResponse({
        error: error?.message ?? 'Failed to scrape questions',
      });
      setQuestions([]);
    } finally {
      setIsLoadingQuestions(false);
    }
  };

  const buildAIRequest = (question) => ({
    conversationId,
    questionKey: question.Key,
    questionValue: question.Question,
    type: question.Type,
    choices: question.Choices || [],
    previousNote: question.ExistingNote ?? question.Structure ?? null,
    downgraded: false,
    structure: question.Structure || {},
  });

  const callAI = async (request) => {
    const response = await apiClient.getConversationQuestion(
      request.conversationId,
      request.questionKey,
      request.questionValue,
      request.type,
      request.choices,
      request.previousNote,
      request.downgraded,
      request.structure,
    );

    if (response?.error) {
      const message =
        typeof response.error === 'string' ? response.error : response.error?.message || 'AI error';
      const error = new Error(message);
      error.rawResponse = response;
      throw error;
    }

    return { response, answer: response?.answer ?? null };
  };

  const askAI = async (index) => {
    if (!conversationId) return;

    const question = questions[index];
    const request = buildAIRequest(question);

    updateQuestion(index, {
      _aiStatus: 'pending',
      _aiAnswer: null,
      _aiError: null,
      _aiRequest: request,
      _aiResponse: null,
    });

    try {
      const { response, answer } = await callAI(request);

      setQuestions((previousQuestions) =>
        previousQuestions.map((currentQuestion, questionIndex) => {
          if (questionIndex !== index) return currentQuestion;

          return {
            ...currentQuestion,
            _aiStatus: 'success',
            _aiAnswer: answer,
            _aiResponse: response,
            _manualAnswer:
              answer != null
                ? formatAnswerForEditor(currentQuestion, answer)
                : currentQuestion._manualAnswer,
          };
        }),
      );
    } catch (error) {
      updateQuestion(index, {
        _aiStatus: 'error',
        _aiError: error?.message,
        _aiResponse: error?.rawResponse ?? null,
      });
    }
  };

  const askAIAll = async () => {
    if (!conversationId || questions.length === 0) return;

    setIsBulkAI(true);
    const pendingRequests = questions.map(buildAIRequest);

    setQuestions((previousQuestions) =>
      previousQuestions.map((question, index) => ({
        ...question,
        _aiStatus: 'pending',
        _aiAnswer: null,
        _aiError: null,
        _aiRequest: pendingRequests[index],
        _aiResponse: null,
      })),
    );

    const settledResults = await Promise.allSettled(
      pendingRequests.map((request) => callAI(request)),
    );

    setQuestions((previousQuestions) =>
      previousQuestions.map((question, index) => {
        const settledResult = settledResults[index];

        if (settledResult.status === 'fulfilled') {
          const { response, answer } = settledResult.value;
          return {
            ...question,
            _aiStatus: 'success',
            _aiAnswer: answer,
            _aiResponse: response,
            _manualAnswer:
              answer != null ? formatAnswerForEditor(question, answer) : question._manualAnswer,
          };
        }

        return {
          ...question,
          _aiStatus: 'error',
          _aiError: settledResult.reason?.message ?? 'Unknown error',
          _aiResponse: settledResult.reason?.rawResponse ?? null,
        };
      }),
    );

    setIsBulkAI(false);
  };

  const insertAnswer = async (index) => {
    const question = questions[index];

    let answer;
    try {
      answer = getAnswerForInsert(question);
    } catch (error) {
      updateQuestion(index, {
        _insertStatus: 'error',
        _insertError: error?.message,
        _insertRequest: null,
        _insertResult: null,
      });
      return;
    }

    const replyType = question['Reply Type'] ?? question.ReplyType ?? null;
    const insertRequest = buildInsertRequest(question, answer, replyType);

    updateQuestion(index, {
      _insertStatus: 'pending',
      _insertError: null,
      _insertRequest: insertRequest,
      _insertResult: null,
    });

    try {
      const result = await emrInjector.answerQuestionInEMR(question.Question, answer, replyType);
      const insertError = getInsertErrorFromResult(result);

      updateQuestion(index, {
        _insertStatus: insertError ? 'error' : 'success',
        _insertError: insertError,
        _insertResult: result,
      });
    } catch (error) {
      updateQuestion(index, {
        _insertStatus: 'error',
        _insertError: error?.message,
      });
    }
  };

  const insertAll = async () => {
    setIsBulkInsert(true);

    const insertTargets = questions.map((question) => {
      const replyType = question['Reply Type'] ?? question.ReplyType ?? null;

      try {
        const answer = getAnswerForInsert(question);
        return {
          answer,
          replyType,
          request: buildInsertRequest(question, answer, replyType),
          error: null,
        };
      } catch (error) {
        return {
          answer: null,
          replyType,
          request: null,
          error: error?.message ?? 'Insert failed',
        };
      }
    });

    setQuestions((previousQuestions) =>
      previousQuestions.map((question, index) => {
        const insertTarget = insertTargets[index];
        return {
          ...question,
          _insertStatus: insertTarget.error ? 'error' : 'pending',
          _insertError: insertTarget.error,
          _insertRequest: insertTarget.request,
          _insertResult: null,
        };
      }),
    );

    const settledResults = await Promise.allSettled(
      questions.map((question, index) => {
        const insertTarget = insertTargets[index];
        if (insertTarget.error) {
          return Promise.resolve(null);
        }
        return emrInjector.answerQuestionInEMR(
          question.Question,
          insertTarget.answer,
          insertTarget.replyType,
        );
      }),
    );

    setQuestions((previousQuestions) =>
      previousQuestions.map((question, index) => {
        const insertTarget = insertTargets[index];
        if (insertTarget.error) return question;

        const settledResult = settledResults[index];
        if (settledResult.status === 'fulfilled') {
          const insertError = getInsertErrorFromResult(settledResult.value);
          return {
            ...question,
            _insertStatus: insertError ? 'error' : 'success',
            _insertError: insertError,
            _insertResult: settledResult.value,
          };
        }

        return {
          ...question,
          _insertStatus: 'error',
          _insertError: settledResult.reason?.message ?? 'Insert failed',
        };
      }),
    );

    setIsBulkInsert(false);
  };

  const onManualChange = (index, value) => {
    updateQuestion(index, { _manualAnswer: value });
  };

  const onToggleCardOpen = (index) => {
    setQuestions((previousQuestions) =>
      previousQuestions.map((question, questionIndex) =>
        questionIndex === index
          ? { ...question, _cardOpen: question._cardOpen === false }
          : question,
      ),
    );
  };

  const setAllQuestionCardsOpen = (isOpen) => {
    setQuestions((previousQuestions) =>
      previousQuestions.map((question) => ({ ...question, _cardOpen: isOpen })),
    );
  };

  const canAnswer = !!conversationId;
  const flexQuestionCount = questions.filter((question) => question.Type === 'Flex').length;
  const answeredQuestionCount = questions.filter(
    (question) => question._manualAnswer !== '',
  ).length;
  const aiIssueCount = questions.filter((question) => question._aiStatus === 'error').length;
  const insertIssueCount = questions.filter(
    (question) => question._insertStatus === 'error',
  ).length;
  const getAnswersRequestValue = buildGetAnswersRequestValue(questions);

  if (!apiClient.canUseCustomBackend()) {
    return <div>You must be a developer to access this feature</div>;
  }

  return (
    <div style={styles.root}>
      <div style={styles.section}>
        <div style={styles.sectionRow}>
          <div style={{ minWidth: 0 }}>
            <div style={styles.eyebrow}>Injector workbench</div>
            <div style={styles.title}>EMR Injector Debug</div>
          </div>

          <div style={{ ...styles.statsRow, width: '100%' }}>
            <StatChip label="Questions" value={questions.length} tone="accent" />
            <StatChip label="Flex" value={flexQuestionCount} />
            <StatChip label="Edited" value={answeredQuestionCount} />
            <StatChip label="AI issues" value={aiIssueCount} tone="warning" />
            <StatChip label="Insert issues" value={insertIssueCount} tone="warning" />
          </div>
        </div>

        <FormGroup style={{ marginBottom: '0.75rem' }}>
          <div style={styles.formLabelRow}>
            <Label for="conversationSelect" style={{ ...styles.panelLabel, marginBottom: 0 }}>
              Conversation
            </Label>
          </div>

          <Input
            id="conversationSelect"
            type="select"
            bsSize="sm"
            value={conversationId}
            onChange={(event) => setConversationId(event.target.value)}
            disabled={conversationsLoading}
            style={styles.select}
          >
            <option value="">
              {conversationsLoading
                ? 'Loading conversations…'
                : conversations.length === 0
                  ? 'No conversations found'
                  : 'Select a conversation…'}
            </option>
            {conversations.map((conversation) => {
              const patientName =
                patientMap[conversation.group] || conversation.group || 'Unknown patient';

              return (
                <option key={conversation.conversationId} value={conversation.conversationId}>
                  {patientName} — {conversation.lastUsed} (#
                  {conversation.conversationId})
                </option>
              );
            })}
          </Input>
        </FormGroup>

        <div style={styles.toolbar}>
          <ActionButton
            disabled={isLoadingPage}
            busy={isLoadingPage}
            onClick={fetchPageInfo}
            style={{ minWidth: '104px' }}
          >
            Get Page Info
          </ActionButton>
          <ActionButton
            disabled={isLoadingPatient}
            busy={isLoadingPatient}
            onClick={fetchPatientInfo}
            style={{ minWidth: '112px' }}
          >
            Get Patient Info
          </ActionButton>
          <ActionButton
            variant="primary"
            disabled={isLoadingQuestions}
            busy={isLoadingQuestions}
            onClick={fetchQuestions}
            style={{ minWidth: '128px' }}
          >
            Get EMR Questions
          </ActionButton>
          <ActionButton
            variant="primaryOutline"
            disabled={isBulkAI || !canAnswer}
            busy={isBulkAI}
            onClick={askAIAll}
            title={canAnswer ? undefined : 'Select a conversation first'}
            style={{ minWidth: '102px' }}
          >
            Ask AI for All
          </ActionButton>
          <ActionButton
            variant="successOutline"
            disabled={isBulkInsert}
            busy={isBulkInsert}
            onClick={insertAll}
            style={{ minWidth: '78px' }}
          >
            Insert All
          </ActionButton>
        </div>
      </div>

      <div style={styles.panelGrid}>
        {(pageInfo || isLoadingPage) && (
          <Disclosure
            framed
            label="getPageInfo() response"
            defaultOpen={false}
            hasContent={!!pageInfo || isLoadingPage}
          >
            {isLoadingPage && !pageInfo ? <Spinner size="sm" /> : <JSONBlock value={pageInfo} />}
          </Disclosure>
        )}

        {(patientInfo || isLoadingPatient) && (
          <Disclosure
            framed
            label="getPatientInfo() response"
            defaultOpen={false}
            hasContent={!!patientInfo || isLoadingPatient}
          >
            {isLoadingPatient && !patientInfo ? (
              <Spinner size="sm" />
            ) : (
              <JSONBlock value={patientInfo} />
            )}
          </Disclosure>
        )}
      </div>

      {(questionsResponse || isLoadingQuestions || questions.length > 0) && (
        <div style={styles.stack}>
          <Disclosure framed label="getQuestions() response">
            {isLoadingQuestions && !questionsResponse ? (
              <Spinner size="sm" />
            ) : (
              <JSONBlock value={questionsResponse} />
            )}
          </Disclosure>

          <Disclosure framed label="getAnswers() request">
            {isLoadingQuestions && questions.length === 0 ? (
              <Spinner size="sm" />
            ) : (
              <JSONBlock value={getAnswersRequestValue} />
            )}
          </Disclosure>
        </div>
      )}

      {questions.length > 0 && (
        <div style={styles.queue}>
          <div style={styles.queueHeader}>
            <div style={styles.queueMeta}>
              <div style={{ ...styles.panelLabel, color: COLORS.accent }}>Work queue</div>
              <div
                style={{
                  fontSize: '1rem',
                  fontWeight: 800,
                  color: COLORS.text,
                }}
              >
                EMR Questions ({questions.length})
              </div>
            </div>

            <div style={styles.queueControls}>
              <ActionButton
                onClick={() => setAllQuestionCardsOpen(true)}
                style={{ padding: '0.38rem 0.72rem' }}
              >
                Expand All
              </ActionButton>
              <ActionButton
                onClick={() => setAllQuestionCardsOpen(false)}
                style={{ padding: '0.38rem 0.72rem' }}
              >
                Collapse All
              </ActionButton>
            </div>
          </div>

          {questions.map((question, questionIndex) => (
            <QuestionCard
              key={question.Key ?? questionIndex}
              question={question}
              questionIndex={questionIndex}
              canAnswer={canAnswer}
              isBulkAI={isBulkAI}
              isBulkInsert={isBulkInsert}
              onAskAI={askAI}
              onInsert={insertAnswer}
              onManualChange={onManualChange}
              onToggleCardOpen={onToggleCardOpen}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default EMRInjectorDebug;
